import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {useForm} from 'react-hook-form'
import {useIntl} from 'react-intl'
import {useGoogleMapsApiLoader} from '../../google-maps-api'
import {useToast} from '../../hooks/use-toast'
import {useCountry} from '../../hooks'
import useMultiSite from '../../hooks/use-multi-site'
import {STORE_LOCATOR_HREF} from '../../constants'
import FindStoreFields from './find-store-fields'
import {fillMissingCoordinates, navigateTo} from '../../utils/utils'

const FindStoreLink = ({prefix = '', addressFieldId, inputProps, onSubmit, onError, formLabel}) => {
    const form = useForm()
    const intl = useIntl()
    const showToast = useToast()
    const mounted = useRef(true)
    const {buildUrl} = useMultiSite()
    const getGoogleMapsAPI = useGoogleMapsApiLoader()
    const countryCode = useCountry()

    useEffect(() => {
        return () => {
            mounted.current = false
        }
    }, [])

    onError =
        onError ||
        (() => {
            showToast({
                title: intl.formatMessage({
                    id: 'find_store_link.error.title',
                    defaultMessage: 'Error obtaining location'
                }),
                status: 'error'
            })
        })

    onSubmit =
        onSubmit ||
        ((data) => {
            navigateTo(`${buildUrl(STORE_LOCATOR_HREF)}?${new URLSearchParams(data)}`)
        })

    // typed text without a picked suggestion is geocoded within the site country
    const submitWithCoordinates = async (data) => {
        let completedData
        try {
            completedData = await fillMissingCoordinates(data, getGoogleMapsAPI, countryCode)
        } catch (e) {
            onError(e)
            return
        }
        if (!mounted.current) return

        if (completedData.latitude === '' || completedData.longitude === '') {
            form.setError('address', {
                message: intl.formatMessage({
                    id: 'find_store_link.error.address_not_found',
                    defaultMessage:
                        'No results found. Please select an address from the suggestions.'
                })
            })
            return
        }

        await onSubmit(completedData)
    }

    return (
        <form onSubmit={form.handleSubmit(submitWithCoordinates)} noValidate>
            <FindStoreFields
                form={form}
                inputProps={inputProps}
                prefix={prefix}
                onLocationChange={form.handleSubmit(submitWithCoordinates)}
                onError={onError}
                addressFieldId={addressFieldId}
                formLabel={formLabel}
                onSubmit={submitWithCoordinates}
            />
        </form>
    )
}

FindStoreLink.propTypes = {
    /** Optional input props to pass to the address field */
    inputProps: PropTypes.object,
    /** Optional prefix for field names */
    prefix: PropTypes.string,
    /** Function to call when form is submitted */
    onSubmit: PropTypes.func,
    /** Function to call when error occurs */
    onError: PropTypes.func,
    addressFieldId: PropTypes.string,
    formLabel: PropTypes.object
}

export default FindStoreLink
